import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Shield,
  Users,
  MessageCircle,
  CheckCircle,
  XCircle,
  Clock,
  ArrowLeft,
  MapPin,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingApplications, setPendingApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, appsRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { credentials: "include" }),
        fetch(`${API}/admin/pending-applications`, { credentials: "include" }),
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (appsRes.ok) {
        setPendingApplications(await appsRes.json());
      }
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/approve-homey/${userId}`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Application approved!");
        fetchData();
      } else {
        toast.error("Failed to approve application");
      }
    } catch (error) {
      toast.error("Error approving application");
    }
  };

  const handleReject = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/reject-homey/${userId}`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Application rejected");
        fetchData();
      } else {
        toast.error("Failed to reject application");
      }
    } catch (error) {
      toast.error("Error rejecting application");
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-12 h-12 border-4 border-[#4A6C6F] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0] px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-[#4A5568]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#4A6C6F] rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#1A202C]">Admin Dashboard</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card data-testid="stat-total" className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#4A6C6F]/10 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#4A6C6F]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A202C]">{stats?.total_users || 0}</p>
                  <p className="text-sm text-[#718096]">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-newbies" className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#48BB78]/10 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#48BB78]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A202C]">{stats?.newbies || 0}</p>
                  <p className="text-sm text-[#718096]">Newbies</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-homeys" className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#D97757]/10 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#D97757]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A202C]">{stats?.local_homeys || 0}</p>
                  <p className="text-sm text-[#718096]">Local Homeys</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-pending" className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#ED8936]/10 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#ED8936]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A202C]">{stats?.pending_applications || 0}</p>
                  <p className="text-sm text-[#718096]">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-conversations" className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#4299E1]/10 rounded-xl flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-[#4299E1]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A202C]">{stats?.active_conversations || 0}</p>
                  <p className="text-sm text-[#718096]">Active Chats</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Applications */}
        <Card data-testid="pending-applications" className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1A202C]">
              <Clock className="w-5 h-5 text-[#ED8936]" />
              Pending Local Homey Applications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingApplications.length === 0 ? (
              <div className="text-center py-8 text-[#718096]">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No pending applications</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Bio</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingApplications.map((app) => (
                    <TableRow key={app.user_id} data-testid={`application-${app.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={app.picture} alt={app.name} />
                            <AvatarFallback className="bg-[#4A6C6F] text-white">
                              {getInitials(app.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{app.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-[#718096]">{app.email}</TableCell>
                      <TableCell className="capitalize">
                        <span className="px-2 py-1 bg-[#F5F5F0] rounded-full text-sm">
                          {app.local_homey_country === "ireland" && "🇮🇪"}
                          {app.local_homey_country === "uk" && "🇬🇧"}
                          {app.local_homey_country === "canada" && "🇨🇦"}{" "}
                          {app.local_homey_country}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-[#718096]">
                        {app.bio || "No bio provided"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            data-testid={`approve-${app.user_id}`}
                            size="sm"
                            onClick={() => handleApprove(app.user_id)}
                            className="bg-[#48BB78] hover:bg-[#38A169] text-white"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            data-testid={`reject-${app.user_id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(app.user_id)}
                            className="border-[#E53E3E] text-[#E53E3E] hover:bg-[#E53E3E]/10"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;
